import { inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Router } from '@angular/router';
import { AuthService as Auth0Angular, User } from '@auth0/auth0-angular';
import { QueryClient } from '@tanstack/angular-query-experimental';
import { Observable } from 'rxjs';
import { jwtDecode } from 'jwt-decode';

import { isUserStatus, UserStatus } from '@core/models/user-status.model';
import { isWorkspaceRole } from '@core/models/workspace-role.model';
import { UserRole } from '@core/models/api/user.api.model';
import { WhoamiUser, DecodedAccessToken } from '@core/models/whoami.model';
import { ClipboardService } from '@core/services/clipboard.service';
import { clearActivity } from '@core/services/session-activity';
import { deleteLegacyQueryStore } from '@core/services/legacy-query-store';
import { forgetRecentSecrets } from '@secrets/services/recent-secrets.service';
import { safeReturnUrl } from '@shared/helpers/return-url.helper';

/**
 * The Action writes the TeamKeys role into the `roles` claim as a single string; tolerate an array too.
 * Unknown values become null so nothing in the UI unlocks by accident.
 */
function parseWorkspaceRole(claim: unknown): UserRole | null {
    const value: unknown = Array.isArray(claim) ? (claim as unknown[])[0] : claim;
    return typeof value === 'string' && isWorkspaceRole(value) ? value : null;
}

/** The Action copies the user's status (`STATUS_INVITED`, `STATUS_ENABLED`, ...) into the `status` claim. */
function parseUserStatus(claim: unknown): UserStatus | null {
    return typeof claim === 'string' && isUserStatus(claim) ? claim : null;
}

@Injectable({
    providedIn: 'root',
})
export class Auth0Service {
    private readonly auth0 = inject(Auth0Angular);
    private readonly router = inject(Router);
    private readonly document = inject(DOCUMENT);
    private readonly queryClient = inject(QueryClient);
    private readonly clipboard = inject(ClipboardService);

    /**
     * Login with Auth0
     * Redirects to Auth0 login page. Auth0 hands `appState.target` back after the login, so it
     * opens the page the user asked for (the login page's `returnUrl`) instead of the login page.
     */
    public login(): Observable<void> {
        return this.auth0.loginWithRedirect({
            appState: {
                target: this.returnTarget(),
            },
            authorizationParams: {
                connection: 'google-oauth2',
            },
        });
    }

    /**
     * Signup with Auth0
     * Redirects to Auth0 with signup hint
     */
    public signup(): Observable<void> {
        return this.auth0.loginWithRedirect({
            appState: {
                target: this.returnTarget(),
            },
            authorizationParams: {
                connection: 'google-oauth2',
                // eslint-disable-next-line @typescript-eslint/naming-convention -- Auth0 API parameter
                screen_hint: 'signup',
            },
        });
    }

    /**
     * Handle the authentication callback
     * Gets the access token after Auth0 SDK has processed the OAuth callback
     * NOTE: The Auth0 Angular SDK handles the OAuth redirect automatically.
     * This method should only be called AFTER the SDK has finished loading (isLoading$ = false)
     * @returns Observable with the access token
     */
    public handleAuthenticationCallback(): Observable<string> {
        return this.auth0.getAccessTokenSilently();
    }

    /**
     * Get access token from Auth0
     * This should only be called after the SDK has finished loading
     * The SDK handles token caching and refresh automatically
     * @returns Observable with the access token
     */
    public getAccessToken(): Observable<string> {
        return this.auth0.getAccessTokenSilently();
    }

    /**
     * Logout from Auth0
     * Redirects to the logout page after wiping what this browser holds of the session.
     */
    public logout(): void {
        this.purgeLocalSession();
        this.auth0.logout({
            logoutParams: {
                returnTo: this.document.location.origin,
            },
        });
    }

    /**
     * Wipe what this browser holds of a session, so the next user of the browser finds nothing
     * (US-2134): the query cache with secrets, users and access lists, a secret still on the
     * clipboard (US-2146), and everything `forgetStoredSession` removes. Runs on every logout.
     */
    public purgeLocalSession(): void {
        this.queryClient.clear();
        this.clipboard.clearNow();
        this.forgetStoredSession();
    }

    /**
     * Forget what a session left in browser storage: the recently opened secrets, a query cache
     * an older build left in IndexedDB, and the shared last-activity moment, so the next login is
     * not judged idle by what happened before it. Also runs on a start without a session, which
     * leaves the in-memory cache alone: a public page (join) may already be loading through it.
     */
    public forgetStoredSession(): void {
        clearActivity();
        forgetRecentSecrets();
        deleteLegacyQueryStore();
    }

    /**
     * Check if the user is authenticated
     * @returns Observable<boolean>
     */
    public isAuthenticated(): Observable<boolean> {
        return this.auth0.isAuthenticated$;
    }

    /**
     * Get the user profile from Auth0
     * @returns Observable with the user profile
     */
    public getProfile(): Observable<User | null | undefined> {
        return this.auth0.user$;
    }

    public getDecodedAccessToken(accessToken: string): DecodedAccessToken {
        return jwtDecode<DecodedAccessToken>(accessToken);
    }

    public parseAccessToken(decodedAccessToken: DecodedAccessToken): WhoamiUser {
        const prefix = 'http://teamkeys.io/';

        const {
            [prefix + 'user_id']: userId = '',
            [prefix + 'tenant_id']: tenantId = '',
            [prefix + 'new_tenant']: isNewTenant = '',
            [prefix + 'new_user']: isNewUser = '',
            [prefix + 'roles']: roles,
            [prefix + 'permissions']: permissions,
            [prefix + 'status']: status,
        } = decodedAccessToken;

        return {
            userId: userId as string,
            tenantId: tenantId as string,
            isNewTenant: isNewTenant as string,
            isNewUser: isNewUser as string,
            role: parseWorkspaceRole(roles),
            permissions: Array.isArray(permissions) ? permissions.filter((p): p is string => typeof p === 'string') : [],
            status: parseUserStatus(status),
        };
    }

    /** The login page's `returnUrl` when it is a page inside the app, else the current url */
    private returnTarget(): string {
        const returnUrl = this.router.parseUrl(this.router.url).queryParamMap.get('returnUrl');

        return safeReturnUrl(returnUrl) ?? this.router.url;
    }
}
